import { useState, useRef } from 'react';
import { getApiBase } from '../../lib/api';

/**
 * 語音轉文字：上傳音檔，Whisper 轉成文字
 */
export function AiSpeechToTextTool() {
  const [file, setFile] = useState<File | null>(null);
  const [text, setText] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const apiBase = getApiBase();

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0];
    setFile(f ?? null);
    setError(null);
    setText('');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!file || !apiBase) {
      if (!apiBase) setError('請設定 VITE_API_BASE 後端網址');
      else setError('請選擇音檔');
      return;
    }
    setError(null);
    setLoading(true);
    try {
      const form = new FormData();
      form.append('file', file);
      const res = await fetch(`${apiBase}/api/ai/speech-to-text`, {
        method: 'POST',
        body: form,
      });
      const data = (await res.json()) as {
        success?: boolean;
        text?: string;
        message?: string;
      };
      if (!res.ok) {
        setError(data.message ?? `錯誤 ${res.status}`);
        setText('');
        return;
      }
      setText(data.text ?? '');
    } catch (err) {
      setError(err instanceof Error ? err.message : '請求失敗');
      setText('');
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <div className="mb-6 rounded-lg border border-gray-200 bg-white p-6 dark:border-gray-700 dark:bg-gray-800">
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
              選擇音檔（MP3、WAV、WebM 等）
            </label>
            <input
              ref={inputRef}
              type="file"
              accept="audio/*"
              onChange={handleFileChange}
              className="w-full rounded border border-gray-300 bg-white px-3 py-2 text-sm dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100"
              disabled={!apiBase}
            />
          </div>
          {!apiBase && (
            <p className="text-sm text-amber-600 dark:text-amber-400">
              請在環境變數設定 VITE_API_BASE 指向後端 API 網址。
            </p>
          )}
          {error && (
            <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
          )}
          <button
            type="submit"
            disabled={loading || !file || !apiBase}
            className="rounded bg-primary px-4 py-2 text-white hover:bg-primary/90 disabled:opacity-50"
          >
            {loading ? '轉換中…' : '語音轉文字'}
          </button>
        </form>
      </div>

      <div className="mb-6 rounded-lg border border-gray-200 bg-gray-50 p-6 dark:border-gray-700 dark:bg-gray-800">
        <h2 className="mb-3 text-sm font-medium text-gray-700 dark:text-gray-300">
          辨識結果
        </h2>
        {text ? (
          <p className="whitespace-pre-wrap text-gray-900 dark:text-gray-100">
            {text}
          </p>
        ) : (
          <p className="text-gray-500 dark:text-gray-400">
            上傳音檔並送出後，將顯示轉錄文字。
          </p>
        )}
      </div>

      <section className="rounded-lg border border-gray-200 bg-white p-6 dark:border-gray-700 dark:bg-gray-800">
        <h2 className="mb-3 text-lg font-semibold text-gray-900 dark:text-gray-100">
          使用說明
        </h2>
        <p className="leading-relaxed text-gray-600 dark:text-gray-400">
          使用 Cloudflare Workers AI 的 Whisper
          模型，將上傳的語音檔轉成文字，支援多種格式（如
          MP3、WAV、WebM）。適合語音備忘錄、會議記錄、字幕產生等情境。
        </p>
      </section>
    </>
  );
}
